import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import type { EntityCloneItem as EntityCloneItemType } from '@web-config-app/core';
import {
  TextInput,
  StackLayout,
  FormField,
} from '@leagueplatform/genesis-core';

export type EntityCloneItemProps = Omit<EntityCloneItemType, 'type'> & {
  onNameChange: (id: string, name: string) => void;
  currentName?: string;
};

/**
 * Input component for renaming an entity during the cloning process.
 *
 * @component
 * @param {EntityCloneItemProps & React.PropsWithChildren} props - The props for the EntityCloneItemInput component.
 * @returns {JSX.Element} The rendered EntityCloneItemInput component.
 */
export const EntityCloneItemInput = ({
  id,
  originalName,
  currentName,
  onNameChange,
  isDuplicate,
  children,
}: React.PropsWithChildren<EntityCloneItemProps>) => {
  const { formatMessage } = useIntl();

  const handleOnChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = evt.currentTarget.value;
    onNameChange(id, newValue);
  };

  return (
    <StackLayout spacing="$half" css={{ paddingBlockEnd: '$half' }}>
      <FormField
        id={id}
        label={formatMessage(
          { id: 'DEEP_CLONE_OPTIONAL_ENTITY_ITEM_RENAME' },
          { originalName },
        )}
        hint={
          isDuplicate
            ? formatMessage({ id: 'DUPLICATE_CHILD_CLONE_NAME_HINT' })
            : undefined
        }
      >
        <TextInput
          value={currentName}
          onChange={handleOnChange}
          readOnly={isDuplicate}
        />
      </FormField>
      {children}
    </StackLayout>
  );
};
